/**
 * GRANEL2 packing: incremental filling-line algorithm.
 *
 * 1. Seed a box (full width row) on the farthest edge (column 0 / front).
 * 2. Draw the filling line from the top-right of the peak plateau → container
 *    lower-right floor (same diagonal format as GRANEL).
 * 3. Revisit layers bottom→top and place boxes while the L×H cross-section
 *    fraction below the line is ≥ minBelowFraction (default 0.3).
 * 4. When no more boxes fit, stack on the furthest column, redraw, repeat.
 * 5. When the furthest column hits the ceiling, only fill-pass placements
 *    under the line remain; stop when neither fill nor raise can progress.
 *
 * No minStepWidth / Δheight constraints — those may emerge naturally.
 */

import { chooseOrientation } from "./orientation";
import { orientationCapacity } from "./types";
import {
  placeBoxes,
  DEFAULT_MIN_BELOW_FRACTION,
} from "./steppedPacker";

export { DEFAULT_MIN_BELOW_FRACTION as GRANEL2_MIN_BELOW_FRACTION };

function diagonalY(z, z0, y0, z1, y1) {
  if (Math.abs(z1 - z0) < 1e-12) return y0;
  const t = (z - z0) / (z1 - z0);
  return y0 + t * (y1 - y0);
}

/**
 * Fraction of the box height that lies below the filling line (L×H view).
 */
function boxBelowFraction(y0, y1, zMid, z0Line, y0Line, z1Line, y1Line) {
  const yLine = diagonalY(zMid, z0Line, y0Line, z1Line, y1Line);
  const h = y1 - y0;
  if (h <= 0) return 0;
  if (yLine <= y0) return 0;
  if (yLine >= y1) return 1;
  return (yLine - y0) / h;
}

/**
 * Line from top-right of the rightmost peak column → container end at floor.
 * @returns {[number, number, number, number]}
 */
function fillingLine(heights, boxH, boxD, contL) {
  if (!heights.length || Math.max(...heights) <= 0) {
    return [0, 0, contL, 0];
  }
  const peak = Math.max(...heights);
  let rightmostPeak = 0;
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] === peak) rightmostPeak = c;
  }
  return [(rightmostPeak + 1) * boxD, peak * boxH, contL, 0];
}

function canPlaceUnderLine(col, level, boxH, boxD, line, minBelow) {
  const [z0, y0, z1, y1] = line;
  const yBox0 = level * boxH;
  const yBox1 = (level + 1) * boxH;
  const zMid = (col + 0.5) * boxD;
  return boxBelowFraction(yBox0, yBox1, zMid, z0, y0, z1, y1) >= minBelow;
}

function stepsFromHeights(heights) {
  const steps = [];
  let i = 0;
  const n = heights.length;
  while (i < n) {
    const h = heights[i];
    if (h <= 0) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < n && heights[j] === h) j += 1;
    steps.push({ startCol: i, numCols: j - i, heightBoxes: h });
    i = j;
  }
  return steps;
}

/**
 * Build column heights for one container via the GRANEL2 filling-line loop.
 * @returns {number[]}
 */
function buildHeights(nz, ny, slots, boxH, boxD, contL, minBelow) {
  const heights = Array(nz).fill(0);
  if (slots <= 0 || nz <= 0 || ny <= 0) return heights;

  // Seed farthest edge (column 0).
  heights[0] = 1;
  let placed = 1;

  let guard = 0;
  const maxGuard = slots * (nz + ny + 2) + 10;

  while (placed < slots && guard++ < maxGuard) {
    const line = fillingLine(heights, boxH, boxD, contL);

    // Fill pass: revisit layers bottom → top under the current line.
    let progressed = false;
    for (let level = 0; level < ny; level++) {
      for (let col = 0; col < nz; col++) {
        if (placed >= slots) break;
        // Next box in this column sits at `level` iff current height is `level`.
        if (heights[col] !== level) continue;
        if (!canPlaceUnderLine(col, level, boxH, boxD, line, minBelow)) {
          continue;
        }
        heights[col] += 1;
        placed += 1;
        progressed = true;
      }
    }

    if (placed >= slots) break;
    if (progressed) continue;

    // No cell fits under the line — raise the furthest column and redraw.
    if (heights[0] < ny) {
      heights[0] += 1;
      placed += 1;
      continue;
    }

    // Furthest column at ceiling: try seeding / growing the next columns
    // that still satisfy the 30% rule (same check as fill; if none, stop).
    let advanced = false;
    for (let col = 1; col < nz; col++) {
      if (placed >= slots) break;
      const level = heights[col];
      if (level >= ny) continue;
      if (!canPlaceUnderLine(col, level, boxH, boxD, line, minBelow)) {
        continue;
      }
      heights[col] += 1;
      placed += 1;
      advanced = true;
      break;
    }
    if (!advanced) break;
  }

  return heights;
}

/**
 * Pack quantity of masters into one container with GRANEL2.
 */
export function packSingleGranel2({
  containerIndex,
  contL,
  contW,
  contH,
  orientation: o,
  quantity,
  kind = "master",
  zOffset = 0,
  minBelowFraction = DEFAULT_MIN_BELOW_FRACTION,
}) {
  const capacity = orientationCapacity(o);
  const qty = Math.max(0, Math.min(quantity, capacity));

  const empty = {
    containerIndex,
    contL,
    contW,
    contH,
    orientation: o,
    quantity: 0,
    capacity,
    boxes: [],
    columnHeights: Array(o.nz).fill(0),
    steps: [],
    diagonal: [0, 0, contL, 0],
  };

  if (qty === 0 || o.nx === 0 || o.ny === 0 || o.nz === 0) {
    return empty;
  }

  const fullLevels = Math.floor(qty / o.nx);
  const remBoxes = qty % o.nx;
  const slots = fullLevels + (remBoxes ? 1 : 0);

  const heights = buildHeights(
    o.nz,
    o.ny,
    slots,
    o.boxH,
    o.boxD,
    contL,
    minBelowFraction
  );

  const { boxes, actualHeights } = placeBoxes(
    heights,
    o.nx,
    qty,
    o.boxW,
    o.boxH,
    o.boxD,
    kind,
    zOffset,
    containerIndex
  );

  const diag = fillingLine(actualHeights, o.boxH, o.boxD, contL);

  return {
    containerIndex,
    contL,
    contW,
    contH,
    orientation: o,
    quantity: boxes.length,
    capacity,
    boxes,
    columnHeights: actualHeights,
    steps: stepsFromHeights(actualHeights),
    diagonal: diag,
  };
}

/**
 * @param {object} input
 * @param {number} input.masterWidth
 * @param {number} input.masterHeight
 * @param {number} input.masterDepth
 * @param {number} input.quantity
 * @param {number} input.contL
 * @param {number} input.contW
 * @param {number} input.contH
 * @param {number} [input.minBelowFraction]
 */
export function packGranel2(input) {
  const {
    masterWidth,
    masterHeight,
    masterDepth,
    quantity,
    contL,
    contW,
    contH,
    minBelowFraction = DEFAULT_MIN_BELOW_FRACTION,
  } = input;

  const qty = Math.max(0, Math.floor(quantity));
  const dimsList = [[contL, contW, contH]];
  const containers = [];
  let remaining = qty;
  let idx = 0;

  while (remaining > 0 || idx === 0) {
    if (idx >= dimsList.length) {
      dimsList.push([...dimsList[dimsList.length - 1]]);
    }
    const [cL, cW, cH] = dimsList[idx];
    const orientation = chooseOrientation(
      masterWidth,
      masterHeight,
      masterDepth,
      cL,
      cW,
      cH
    );
    const cap = orientationCapacity(orientation);

    if (cap <= 0) {
      containers.push({
        containerIndex: idx,
        contL: cL,
        contW: cW,
        contH: cH,
        orientation,
        quantity: 0,
        capacity: 0,
        boxes: [],
        columnHeights: [],
        steps: [],
        diagonal: [0, 0, cL, 0],
      });
      break;
    }

    const take = remaining > 0 ? Math.min(remaining, cap) : 0;
    const result = packSingleGranel2({
      containerIndex: idx,
      contL: cL,
      contW: cW,
      contH: cH,
      orientation,
      quantity: take,
      kind: "master",
      minBelowFraction,
    });
    containers.push(result);
    remaining -= result.quantity;
    idx += 1;
    if (remaining <= 0) break;
    if (idx > 100) break;
  }

  const primary =
    containers[0]?.orientation ??
    chooseOrientation(masterWidth, masterHeight, masterDepth, contL, contW, contH);

  return {
    orientation: primary,
    containers,
    totalQuantity: qty,
    totalCapacity: containers.reduce((s, c) => s + c.capacity, 0),
  };
}
